import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import useCruiseDealsPage from '~/components/Cruises/context/useCruiseDealsPage';

import useAppNavigation from '~/hooks/useAppNavigation';

import { formatDateShort } from '~/services/TimeService';
import dealPageService from '~/services/cruises/DealPageService';

import cruiseDealStatus from '~/utils/cruiseDealStatus';

import { CruiseDealPage } from '../types';

import DealPageCloneConfirmation from './DealPageCloneConfirmation';
import DealPageSearchForm from './DealPageSearchForm';

export default function DealPageList() {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteButton, setDeleteButton] = useState(null);
  const [clonedDealId, setClonedDealId] = useState<string | null>(null);

  const {
    dealsPage,
    page,
    total,
    recordsPerPage,
    paginate,
    getDealsPage,
    isLoading,
    filters,
    setFilters,
    activeTab,
    setActiveTab,
  } = useCruiseDealsPage();

  const { push } = useAppNavigation();

  const handleDeleteDeal = useCallback(
    async (id: string) => {
      try {
        await dealPageService.deleteById(id);
        enqueueSnackbar('Deal Page deleted', { variant: 'success' });
      } catch (error) {
        if (error.status === 404) {
          enqueueSnackbar('This deal page is already deleted', { variant: 'error' });
        } else {
          enqueueSnackbar('Error deleting Deal Page', { variant: 'error' });
        }
      }
    },
    [enqueueSnackbar],
  );

  const actionColumn = useCallback(
    (cell, row) => (
      <Box>
        {deleteButton !== row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              href={`${window.configs.LUX_CUSTOMER_PORTAL}/cruises/deal/${row.slug || row.id}`}
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                push(`/cruises/deals/${row.id}/edit`);
              }}
            >
              Edit
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setClonedDealId(row.id);
              }}
            >
              Clone
            </Button>

            <Button
              variant="text"
              size="small"
              color="error"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(row.id);
              }}
            >
              Delete
            </Button>
          </Box>
        )}

        {deleteButton === row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await handleDeleteDeal(row.id);
                getDealsPage();
              }}
            >
              Confirm
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    ),
    [deleteButton, handleDeleteDeal, getDealsPage, push],
  );

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Deal Page Name',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
    },
    {
      field: 'cruiseLine',
      headerName: 'Cruise Line',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => params.row.cruiseLine.name,
      display: 'flex',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 2,
      renderCell: (params) => {
        const status = cruiseDealStatus({ startDate: params.row.startDate, endDate: params.row.endDate });
        return status?.status;
      },
      display: 'flex',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 270,
      sortable: false,
      renderCell: (params) => actionColumn(params.value, params.row),
      display: 'flex',
    },
  ];

  const handleCloseCloneDialog = useCallback(() => {
    setClonedDealId(null);
  }, []);

  return (
    <Box mt={1}>
      <DealPageSearchForm isLoading={isLoading} filters={filters} onSearch={setFilters} />

      {clonedDealId && (
        <DealPageCloneConfirmation
          open
          dealId={clonedDealId}
          onClose={handleCloseCloneDialog}
          onCloneSuccess={getDealsPage}
        />
      )}
      <Tabs
        value={activeTab}
        onChange={(_, value) => {
          setActiveTab(value);
        }}
        sx={{ mt: 2 }}
      >
        <Tab label="Live" value="live" />
        <Tab label="Inactive" value="inactive" />
      </Tabs>
      <Stack mt={3} width="100%" direction="row" justifyContent="flex-end" spacing={2}>
        <Button component={Link} to="/cruises/deals/ordering" variant="outlined" size="small">
          Deal Page Ordering
        </Button>
        <Button component={Link} to="/cruises/deals/create" variant="outlined" size="small">
          Add New Deal Page
        </Button>
      </Stack>
      <DataGrid
        loading={isLoading}
        rows={dealsPage || []}
        columns={columns}
        pagination
        paginationModel={{ page: page - 1, pageSize: recordsPerPage }}
        pageSizeOptions={[10]}
        getRowId={(row: CruiseDealPage) => row.id}
        paginationMode="server"
        rowCount={total}
        onPaginationModelChange={({ page }) => paginate(page + 1)}
        slots={{ pagination: GridPagination }}
        autoHeight
      />
    </Box>
  );
}
